
import {
    ActionResponse,
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    useLayoutLifeCycle,
} from '@/Shared';

import { MandateHeader } from '../mandate-config';
import { useMandateEnricher } from '../Hooks';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useDocument, useMandate } from '@/OneLiners';
import { MandateViewModel } from '../ViewModels/MandateViewModel';
import { MandateModel } from '../Models';

/**
 * useMandateTableConfig hook
 *
 * Domain-specific layout composition hook for Mandate entities.
 * Orchestrates domain data, enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate Mandate domain state, enrichment, and permissions into a single response.
 * - Provide dynamic headers for Mandate table.
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
*  @see DomainHookProps
 * @see DomainTableConfig<MandateViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<MandateViewModel>
 */
export function useMandateTableConfig(props: DomainHookProps): DomainTableConfig<MandateViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Mandate;

    /**
     * Domain enums for headers and VM mapping.
     */
    const { MandateEnums, DocumentEnums, NeedEnums, PropertyEnums } = useAppEnum();

    /**
     * Mandate domain entry point (CRUD and state).
     */
    const mandateHook = useMandate();

    /**
    * Document domain state (related documents for Mandate).
    */
    const {
        dataMap: documentMap,
        stateResponse: documentResponse,
    } = useDocument();


    /**
     * Mandate enrichment layer.
     *
     * Resolves derived fields, user and contact data.
     */
    const {
        userMap,
        contactMap,
        getStrictEnrichedMandate,
        enricherResponse,
    } = useMandateEnricher();

    /**
    * Combined readiness response.
    *
    * The layout lifecycle initializes only once this
    * contract reaches a succeeded state.
    */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            mandateHook.stateResponse.status,
            documentResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: mandateHook.stateResponse.error ??
            documentResponse.error ??
            enricherResponse.error
    };

    /**
       * Delegation to generic layout lifecycle.
       */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: mandateHook,

        /**
         * Header configuration for Property table.
         */
        headers: MandateHeader.build(contactMap, userMap, MandateEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (
            mandate: MandateModel, isDisplaying: boolean) =>
            new MandateViewModel({
                mandate: getStrictEnrichedMandate(mandate),
                viewEnums: MandateEnums,
                DocumentEnums,
                NeedEnums,
                PropertyEnums,
                documentMap,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}